import { useEffect } from 'react';
import Layout from '../../../components/layout/Layout';
import Breadcrumb from '../../../components/layout/Breadcrumb';
import { updateSEO } from '../../../utils/seo';

// ===== 専門資格ページ 新デザイン全面最新化（型正典: src/pages/qualifications/business-manner/page.tsx） =====
// 設計note（共通骨格・9ページ共通の設計言語）:
//   紺#1a2942 / 真鍮金#a38e65 / brass2 #8f7a56 / gold-signboard #d7b779 / cream #f8f6f0 / line #e2ddd2。
//   見出し=jba-display（Shippori Mincho B1）+ 金差し色span。eyebrow=Playfair uppercase。BTN-08 2状態。
//   実文出典（創作禁止）: 現行working tree実文の逐語（scratchpad/qual-content.md §4 wine-steward）。
//     数値・受講料(58,000円)・章立て・FAQ(14問)は逐語継承。受験資格20歳以上・ソムリエ資格と異なる旨を明示。
// wine-steward = Heritage型（個性センターピース）:
//   ①H1和名サブ無し（実データ通り・英名のみ）。
//   ②Section I 直後に Heritage(紺パネル引用) を挿入（第1章「執事とセラーマスター」の逐語趣旨）。
//   ③Section III は紺重複回避のため明色(cream)版に変更。
//   セクション: Hero → I → Heritage → II → III(cream) → IV → V → VI → VII → VIII。

export default function WineStewardButlerPage() {
  useEffect(() => {
    updateSEO({
      title: 'Wine Steward Butler資格 | 全日本執事協会',
      description: '全日本執事協会のWine Steward Butler資格制度。ワインサービスの専門知識とテイスティング技術を習得し、上質なサービスを提供できる資格です。',
      keywords: 'Wine Steward Butler,ワイン,酒類,専門家,認定,全日本執事協会',
      canonical: 'https://butler-association.jp/qualifications/wine-steward'
    });
  }, []);

  const breadcrumbs = [
    { label: 'ホーム', href: '/' },
    { label: '資格制度', href: '/qualifications' },
    { label: 'Wine Steward Butler' }
  ];

  // BTN-08（canon と同一文法・2状態）
  const BTN08_ON_LIGHT = "relative inline-flex items-center justify-center bg-[#1a2942] text-[#f8f6f0] border border-[#a38e65] px-[42px] py-[16px] text-sm tracking-[0.2em] font-['Noto_Serif_JP',serif] transition-colors duration-300 hover:bg-[#a38e65] hover:text-[#1a2942] hover:border-[#1a2942] cursor-pointer whitespace-nowrap before:content-[''] before:absolute before:inset-1 before:border before:border-[#a38e65]/45 before:pointer-events-none before:transition-colors before:duration-300 hover:before:border-[#1a2942]/45";
  const BTN08_ON_NAVY = "relative inline-flex items-center justify-center bg-[#a38e65] text-white font-semibold border border-[#1a2942] px-[42px] py-[16px] text-sm tracking-[0.2em] font-['Noto_Serif_JP',serif] transition-colors duration-300 hover:bg-[#1a2942] hover:text-[#f8f6f0] hover:border-[#a38e65] cursor-pointer whitespace-nowrap before:content-[''] before:absolute before:inset-1 before:border before:border-[#1a2942]/40 before:pointer-events-none before:transition-colors before:duration-300 hover:before:border-[#a38e65]/50";

  const features = [
    { icon: 'ri-goblet-line', title: 'ワイン知識', desc: '世界のワイン産地と品種の理解。フランス・イタリアから新世界まで、産地ごとの個性と品種の特徴を体系的に学びます。' },
    { icon: 'ri-hand-heart-line', title: 'サービス実技', desc: '優雅な提供技術とマナー。グラスの選択、デキャンタージュ、注ぎ方とタイミングまで、執事流のサービス作法を習得します。' },
    { icon: 'ri-book-open-line', title: '総合的理解', desc: 'ワイン以外の酒類も網羅。ウイスキー・ブランデー・日本酒・焼酎まで、幅広い酒類の知識を身につけます。' },
  ];

  const curriculum = [
    { no: '01', title: '執事とワインの歴史', desc: '中世ヨーロッパの執事とワイン管理、執事がセラーマスターを兼ねた時代、貴族の晩餐とワインサービスの歴史、現代の執事に求められるワイン知識を学びます。' },
    { no: '02', title: 'ワインの基礎知識', desc: 'ワインの種類と分類、ぶどう品種の特徴、ワイン製造の基本工程、ラベルの読み方と理解を習得します。' },
    { no: '03', title: '世界のワイン産地', desc: 'フランスワイン（ボルドー、ブルゴーニュ等）、イタリアワイン、その他ヨーロッパ諸国、新世界のワイン産地と特徴を学びます。' },
    { no: '04', title: 'ワインの保管と管理', desc: 'セラーの歴史と執事の役割、適切な保管温度と湿度、セラー管理の実践技術、熟成と飲み頃の見極め方を習得します。' },
    { no: '05', title: 'ワインサービスの実技', desc: '執事流のエレガントなサービス作法、グラスの選択と準備、デキャンタージュの技術、注ぎ方とタイミングの見極めを学びます。' },
    { no: '06', title: '料理とワインのペアリング', desc: 'マリアージュの基本原則、料理別のワイン選択方法、チーズとワインの組み合わせ、デザートワインの活用を習得します。' },
    { no: '07', title: 'テイスティングの技術', desc: '執事としての品質評価の視点、視覚・嗅覚・味覚での評価方法、テイスティングの手順、ワインの表現方法と良質なワインの見極めを学びます。' },
    { no: '08', title: 'ウイスキー・ブランデー・スピリッツ', desc: '蒸留酒の基礎知識、ウイスキーの種類と特徴、ブランデー・コニャックの世界、カクテルの基本を習得します。' },
    { no: '09', title: '日本酒・焼酎の知識', desc: '和の酒と執事の関わり、日本酒の基礎知識と分類、焼酎の種類と特徴、和食とのペアリングを学びます。' },
    { no: '10', title: '総合演習', desc: 'シチュエーション別のワイン選択実践、ホスト役のサポート演習、確認テストで学習内容を統合します。' },
  ];

  const recommend = [
    'ワインの専門知識を深めたい方', 'ホテル・レストランで働く方', 'ソムリエ資格の補完として学びたい方',
    'ホームパーティーで活かしたい方', '飲料サービスのスキルアップを目指す方', 'バーテンダーやサービススタッフ',
    '接待や会食が多いビジネスパーソン', '執事資格と組み合わせて専門性を高めたい方',
  ];

  const materials = [
    { no: '1', title: 'テキスト教材（約100ページ）', desc: 'ワインと酒類の専門知識を詳しく解説した専用テキスト。' },
    { no: '2', title: 'オンライン動画レッスン', desc: 'サービス実技やテイスティング方法を含む動画解説。視聴期間1年間。' },
    { no: '3', title: 'ワイン産地マップ', desc: '世界の主要ワイン産地を視覚的に理解できる参考資料。' },
    { no: '4', title: '認定試験問題・解答用紙', desc: '筆記試験とテイスティングレポート課題を含む試験問題。' },
  ];

  const exam = [
    { label: '試験形式', value: '筆記試験（記述式・選択式）+ テイスティングレポート' },
    { label: '合格基準', value: '80点以上' },
    { label: '試験期限', value: '申込から1年以内' },
    { label: '受験資格', value: '特になし（Associate Butler以上推奨、20歳以上）' },
    { label: '受験場所', value: '自宅（通信試験）' },
    { label: '再試験', value: '再試験料10,000円で何度でも可' },
  ];

  const included = [
    'テキスト教材（約100ページ）', 'オンライン動画レッスン（1年間視聴可能）', 'ワイン産地マップ',
    '認定試験問題・解答用紙', '試験添削サービス', '合格時の認定証・認定カード', '送料',
  ];

  const related = [
    { href: '/qualifications/dining-coordinator', name: 'Dining Coordinator Butler資格', ja: '食事の総合的なコーディネートを学ぶ専門資格', icon: 'ri-restaurant-line' },
    { href: '/qualifications/event-planning', name: 'Event Planning Butler資格', ja: 'イベント企画・運営の専門知識を習得', icon: 'ri-calendar-event-line' },
    { href: '/qualifications', name: '専門資格一覧を見る', ja: '全ての専門資格をご覧いただけます', icon: 'ri-list-check' },
  ];

  const faqs = [
    { q: 'ソムリエ資格との違いは何ですか？', a: 'ソムリエ資格は飲食業界の専門資格、本資格は執事視点での飲料サービス知識です。補完的に学ぶこともできます。' },
    { q: 'ワイン初心者でも取得できますか？', a: 'はい、基礎から学べる内容です。ワインの知識がゼロでも段階的に理解できるカリキュラムです。' },
    { q: '実際にワインを飲む必要がありますか？', a: '試験では実際の飲酒は不要です。学習内容に基づいた記述課題で評価します。' },
    { q: '20歳未満は受験できませんか？', a: '申し訳ございませんが、飲酒に関する知識を扱うため、20歳以上の方のみ受験可能です。' },
    { q: 'レストランで働いていますが役立ちますか？', a: 'はい、ワインサービスの質向上に直結する実践的な内容です。' },
    { q: '日本酒やウイスキーも学べますか？', a: 'はい、第8章でウイスキー・ブランデー、第9章で日本酒・焼酎を学びます。' },
    { q: 'セラー管理について詳しく学べますか？', a: 'はい、第4章でセラーの歴史から実践的な保管技術まで詳しく学びます。' },
    { q: 'デキャンタージュの技術も学べますか？', a: 'はい、第5章で実技として詳しく学びます。動画レッスンで手順を確認できます。' },
    { q: '料理とワインのペアリングを学べますか？', a: 'はい、第6章でマリアージュの原則と実践的な選び方を学びます。' },
    { q: 'テイスティングレポート課題とは何ですか？', a: '与えられたワインの情報をもとに、特徴や評価を記述する筆記課題です。実際の飲酒は不要です。' },
    { q: 'ホームパーティーで活用できますか？', a: 'はい、ゲストに合わせたワイン選びやサービス方法など実用的な内容です。' },
    { q: 'ワイン産地の暗記が必要ですか？', a: '主要産地の特徴を理解することが重要です。丸暗記より理解を重視します。' },
    { q: 'カクテルについても学べますか？', a: 'はい、第8章でカクテルの基本知識を学びます。' },
    { q: '執事としてワイン知識が重要な理由は？', a: '第1章で執事とワインの歴史的関係を学び、その重要性を理解します。' },
  ];

  return (
    <Layout>
      {/* ===== Hero — 全幅ビルボード型A（画像+紺グラデ+左寄せ）。H1和名サブ無し（実データ通り英名のみ） ===== */}
      <section className="relative bg-[#1a2942] overflow-hidden min-h-[540px] lg:min-h-[620px] flex items-center">
        <img
          src="/images/generated/jba-p-026-qual-wine-w2.webp"
          alt="ワインスチュワード研修風景。執事がワインを扱い、飲料サービスを提供する様子"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(16,20,26,0.93)_0%,rgba(16,20,26,0.74)_46%,rgba(16,20,26,0.34)_100%)]"></div>
        <div className="relative z-10 w-full max-w-[1280px] mx-auto px-5 sm:px-8 lg:px-10 pt-32 pb-16 lg:pt-36 lg:pb-20">
          <div className="max-w-[680px]">
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.3em] text-xs sm:text-[13px] text-[#d7b779] mb-4">Specialist Certification</span>
            <div className="w-12 h-px bg-[#a38e65] mb-6"></div>
            <h1 className="jba-display font-normal text-white text-[clamp(30px,4vw,50px)] leading-[1.3] tracking-[0.04em] mb-6">Wine Steward Butler</h1>
            <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-white/90 leading-[1.95] max-w-[600px]">
              Wine Steward Butler資格は、ワインと飲料に関する専門知識を持つプロフェッショナルとしての資格です。世界各国のワイン知識、適切な提供方法、保管管理、料理との相性、テイスティング技術など、飲料サービスのプロフェッショナルとしての能力を証明します。
            </p>
          </div>
        </div>
      </section>

      {/* パンくず: ビルボード直後に配置（Yuichi 2026-07-17 指摘②）。共通Breadcrumbのヘッダー逃げpaddingを子セレクタで通常paddingへ上書き */}
      <div className="bg-white [&>div]:!pt-6">
        <Breadcrumb items={breadcrumbs} />
      </div>

      <div className="bg-white">
        <div className="max-w-[1120px] mx-auto px-5 sm:px-8 lg:px-10 py-14 sm:py-20 space-y-16 sm:space-y-24">

          {/* ===== I. 資格概要（リード3段落+細罫データ行+特徴3枚）。由来: about/overview S1 ===== */}
          <section>
            <span className="-mx-5 sm:-mx-8 lg:-mx-10 mb-6 bg-[#1a2942] border-y border-[#a38e65]/25 px-5 sm:px-8 lg:px-10 py-[18px] block font-['Playfair_Display',serif] uppercase tracking-[0.28em] text-xs text-[#d7b779]">I. About This Certification</span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-5">
              ソムリエの知識を、<span className="text-[#a38e65]">執事のサービス</span>へ
            </h2>
            <div className="space-y-[18px] font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] max-w-3xl">
              <p>
                Wine Steward Butler資格は、ワインと飲料に関する専門知識を持つプロフェッショナルとしての資格です。世界各国のワイン知識、適切な提供方法、保管管理、料理との相性、テイスティング技術など、飲料サービスのプロフェッショナルとしての能力を証明します。
              </p>
              <p>
                ソムリエの知識を執事サービスに応用し、ホームパーティーや晩餐会での洗練された飲料サービスを提供できる専門性の高い資格です。
              </p>
              <p>
                ワインだけでなく日本酒やウイスキーなど幅広い酒類の知識も習得します。
              </p>
            </div>

            {/* 細罫データ行 */}
            <dl className="grid grid-cols-2 md:grid-cols-4 gap-y-6 gap-x-4 mt-10 border-t border-[#e2ddd2] pt-8">
              <div>
                <dt className="font-['Noto_Serif_JP',serif] text-xs text-[#8f7a56] tracking-[0.06em] mb-1.5">受講料（税込）</dt>
                <dd className="font-['Playfair_Display',serif] text-2xl text-[#1a2942]">58,000<span className="text-sm text-[#8f7a56] ml-1">円</span></dd>
              </div>
              <div>
                <dt className="font-['Noto_Serif_JP',serif] text-xs text-[#8f7a56] tracking-[0.06em] mb-1.5">学習期間目安</dt>
                <dd className="font-['Playfair_Display',serif] text-2xl text-[#1a2942]">2–4<span className="text-sm text-[#8f7a56] ml-1">ヶ月</span></dd>
              </div>
              <div>
                <dt className="font-['Noto_Serif_JP',serif] text-xs text-[#8f7a56] tracking-[0.06em] mb-1.5">カリキュラム</dt>
                <dd className="font-['Playfair_Display',serif] text-2xl text-[#1a2942]">全10<span className="text-sm text-[#8f7a56] ml-1">章</span></dd>
              </div>
              <div>
                <dt className="font-['Noto_Serif_JP',serif] text-xs text-[#8f7a56] tracking-[0.06em] mb-1.5">受験資格</dt>
                <dd className="font-['Noto_Serif_JP',serif] text-lg text-[#1a2942] leading-tight pt-1">20歳以上・永久</dd>
              </div>
            </dl>

            {/* 特徴3枚（アイコン編集カード） */}
            <div className="grid sm:grid-cols-3 gap-5 sm:gap-6 mt-12">
              {features.map((f) => (
                <div key={f.title} className="border-t-2 border-[#a38e65] bg-[#f8f6f0] px-6 py-7">
                  <i className={`${f.icon} text-3xl text-[#a38e65]`}></i>
                  <h3 className="jba-display font-medium text-[#1a2942] text-lg mt-4 mb-2.5">{f.title}</h3>
                  <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.9]">{f.desc}</p>
                </div>
              ))}
            </div>

            <div className="mt-8 flex items-start gap-3 border border-[#a38e65]/40 bg-[#f8f6f0] px-5 py-4">
              <i className="ri-information-line text-[#a38e65] text-xl mt-0.5"></i>
              <p className="font-['Noto_Serif_JP',serif] text-sm text-[#6b6353] leading-[1.85]">
                本資格は全日本執事協会が認定する民間資格です。ソムリエ資格や日本ソムリエ協会の資格とは異なります。受験資格は20歳以上です（飲酒に関する知識を扱うため）。
              </p>
            </div>
          </section>

          {/* ===== Heritage(紺パネル引用)= about/overview S3 由来。wine の個性センターピース。第1章の逐語調 ===== */}
          <section>
            <span className="-mx-5 sm:-mx-8 lg:-mx-10 mb-6 bg-[#1a2942] border-y border-[#a38e65]/25 px-5 sm:px-8 lg:px-10 py-[18px] block font-['Playfair_Display',serif] uppercase tracking-[0.28em] text-xs text-[#d7b779]">— Heritage</span>
            <div className="bg-[#1a2942] px-7 sm:px-12 lg:px-16 py-12 sm:py-14">
              <div className="text-[#d7b779] font-['Playfair_Display',serif] text-5xl leading-none mb-4">“</div>
              <blockquote className="jba-display font-normal text-[#f8f6f0] text-xl sm:text-2xl lg:text-[28px] leading-[1.7] tracking-[0.02em] max-w-3xl">
                中世ヨーロッパの執事は、貴族のセラーを預かるセラーマスターでもありました。ワインの管理は、執事に託された信頼の証だったのです。
              </blockquote>
              <div className="w-11 h-px bg-[#a38e65] mt-8"></div>
            </div>
          </section>

          {/* ===== II. カリキュラム（番号2カラム編集ブロック+画像）。由来: about/overview S4 ===== */}
          <section>
            <span className="-mx-5 sm:-mx-8 lg:-mx-10 mb-6 bg-[#1a2942] border-y border-[#a38e65]/25 px-5 sm:px-8 lg:px-10 py-[18px] block font-['Playfair_Display',serif] uppercase tracking-[0.28em] text-xs text-[#d7b779]">II. Curriculum</span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-3">
              学ぶ内容 — <span className="text-[#a38e65]">全10章</span>
            </h2>
            <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] max-w-3xl mb-8">
              ワインと酒類の専門知識を、10の分野で体系的に学びます。各章は実践的な内容で構成され、知識だけでなくサービスの技術も習得できます。
            </p>
            <img
              src="/images/generated/jba-fs-183-qualifications_wine-steward-v7.webp"
              alt="ワイン学習カリキュラムの教材。世界のワイン産地やテイスティングを解説するテキストと資料"
              className="w-full aspect-[21/9] h-auto object-cover object-center border border-[#a38e65]/30 mb-10"
            />
            <div className="grid md:grid-cols-2 gap-x-10 gap-y-8">
              {curriculum.map((c) => (
                <div key={c.no} className="flex gap-5 border-t border-[#e2ddd2] pt-6">
                  <span className="font-['Playfair_Display',serif] text-[#d7b779] text-3xl leading-none shrink-0 pt-1">{c.no}</span>
                  <div>
                    <h3 className="jba-display font-medium text-[#1a2942] text-lg mb-2 leading-[1.5]">{c.title}</h3>
                    <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.9]">{c.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* ===== III. こんな方におすすめ（明色cream版・紺重複回避）= wine の個性。由来: about/overview S3 の明色翻案 ===== */}
          <section>
            <span className="-mx-5 sm:-mx-8 lg:-mx-10 mb-6 bg-[#1a2942] border-y border-[#a38e65]/25 px-5 sm:px-8 lg:px-10 py-[18px] block font-['Playfair_Display',serif] uppercase tracking-[0.28em] text-xs text-[#d7b779]">III. Who It's For</span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
              こんな方におすすめです
            </h2>
            <div className="bg-[#f8f6f0] border border-[#a38e65]/25 px-6 sm:px-10 lg:px-12 py-10 sm:py-12">
              <div className="grid sm:grid-cols-2 gap-x-10 gap-y-4">
                {recommend.map((r) => (
                  <div key={r} className="flex items-start gap-3 border-b border-[#e2ddd2] pb-4">
                    <i className="ri-check-line text-[#a38e65] text-xl mt-0.5 shrink-0"></i>
                    <span className="font-['Noto_Serif_JP',serif] text-[15px] text-[#333] leading-[1.8]">{r}</span>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* ===== IV. お届けする教材（番号カード+画像）。由来: about/overview S2 の罫線カード文法 ===== */}
          <section>
            <span className="-mx-5 sm:-mx-8 lg:-mx-10 mb-6 bg-[#1a2942] border-y border-[#a38e65]/25 px-5 sm:px-8 lg:px-10 py-[18px] block font-['Playfair_Display',serif] uppercase tracking-[0.28em] text-xs text-[#d7b779]">IV. Course Materials</span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
              お届けする教材
            </h2>
            <div className="grid lg:grid-cols-[1fr_360px] gap-8 lg:gap-12 items-start">
              <div className="grid sm:grid-cols-2 gap-x-8 gap-y-7">
                {materials.map((m) => (
                  <div key={m.no} className="flex gap-4 border-t border-[#e2ddd2] pt-5">
                    <span className="font-['Playfair_Display',serif] text-[#a38e65] text-xl w-8 h-8 shrink-0 flex items-center justify-center border border-[#a38e65]/40 rounded-full">{m.no}</span>
                    <div>
                      <h3 className="jba-display font-medium text-[#1a2942] text-base mb-1.5 leading-[1.5]">{m.title}</h3>
                      <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.85]">{m.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <img
                src="/images/generated/jba-fs-184-qualifications_wine-steward-v5.webp"
                alt="ワイン教材一式。テキスト・動画レッスン・ワイン産地マップがそろった学習セット"
                className="w-full h-56 lg:h-[300px] object-cover border border-[#a38e65]/30"
              />
            </div>
          </section>

          {/* ===== V. 認定試験について（紺罫定義リスト）。由来: about/overview S2 の border-t-2 定義リスト文法 ===== */}
          <section>
            <span className="-mx-5 sm:-mx-8 lg:-mx-10 mb-6 bg-[#1a2942] border-y border-[#a38e65]/25 px-5 sm:px-8 lg:px-10 py-[18px] block font-['Playfair_Display',serif] uppercase tracking-[0.28em] text-xs text-[#d7b779]">V. Examination</span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
              認定試験について
            </h2>
            <dl className="border-t-2 border-[#1a2942] max-w-3xl">
              {exam.map((row) => (
                <div key={row.label} className="flex flex-col sm:flex-row gap-1 sm:gap-6 border-b border-[#e2ddd2] py-4">
                  <dt className="sm:w-[140px] sm:shrink-0 font-['Noto_Serif_JP',serif] text-sm text-[#8f7a56] tracking-[0.06em] pt-0.5">{row.label}</dt>
                  <dd className="flex-1 font-['Noto_Serif_JP',serif] text-[15px] sm:text-base text-[#1a2942] leading-[1.85]">{row.value}</dd>
                </div>
              ))}
            </dl>
            <p className="font-['Noto_Serif_JP',serif] text-sm text-[#6b6353] leading-[1.9] mt-5 max-w-3xl">
              テイスティングレポートは実際にワインを飲む必要はなく、学習内容に基づいた記述課題です。試験は自宅で行う通信試験で、教材が届いたらご自身のペースで学習を進め、準備ができた段階で試験問題に取り組みます。解答用紙を返送いただくと、当協会が添削・採点し、80点以上で合格。認定証と認定カードを発行します。
            </p>
          </section>

          {/* ===== VI. 料金とお申込み（大字serif受講料+含まれるもの）。受講料は clamp 大字serif強調・受験資格20歳以上を明示 ===== */}
          <section>
            <span className="-mx-5 sm:-mx-8 lg:-mx-10 mb-6 bg-[#1a2942] border-y border-[#a38e65]/25 px-5 sm:px-8 lg:px-10 py-[18px] block font-['Playfair_Display',serif] uppercase tracking-[0.28em] text-xs text-[#d7b779]">VI. Enrollment</span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
              料金とお申込み
            </h2>
            <div className="bg-[#f8f6f0] border border-[#a38e65]/30 px-6 sm:px-10 lg:px-12 py-10">
              <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 border-b border-[#e2ddd2] pb-8 mb-8">
                <div>
                  <p className="font-['Noto_Serif_JP',serif] text-sm text-[#8f7a56] tracking-[0.08em] mb-2">受講料（税込・送料込）</p>
                  <div className="jba-display text-[#1a2942] leading-none">
                    <span className="font-['Playfair_Display',serif] text-[clamp(44px,7vw,64px)] font-semibold">58,000</span>
                    <span className="text-2xl sm:text-3xl ml-1">円</span>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-6 text-center sm:text-right">
                  <div>
                    <p className="font-['Noto_Serif_JP',serif] text-xs text-[#8f7a56] mb-1">学習期間目安</p>
                    <p className="jba-display text-[#1a2942] text-base">2〜4ヶ月</p>
                  </div>
                  <div>
                    <p className="font-['Noto_Serif_JP',serif] text-xs text-[#8f7a56] mb-1">試験期限</p>
                    <p className="jba-display text-[#1a2942] text-base">1年以内</p>
                  </div>
                  <div>
                    <p className="font-['Noto_Serif_JP',serif] text-xs text-[#8f7a56] mb-1">資格有効期限</p>
                    <p className="jba-display text-[#1a2942] text-base">永久</p>
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-2.5 border-b border-[#e2ddd2] pb-5 mb-7 font-['Noto_Serif_JP',serif] text-sm text-[#8f7a56] leading-[1.85]">
                <i className="ri-goblet-line text-[#a38e65] text-base mt-0.5 shrink-0"></i>
                <span>受験資格：20歳以上（飲酒に関する知識を扱うため）</span>
              </div>
              <h3 className="jba-display font-medium text-[#1a2942] text-lg mb-5">料金に含まれるもの</h3>
              <div className="grid sm:grid-cols-2 gap-x-8 gap-y-3">
                {included.map((it) => (
                  <div key={it} className="flex items-start gap-3">
                    <i className="ri-check-line text-[#a38e65] text-lg mt-0.5 shrink-0"></i>
                    <span className="font-['Noto_Serif_JP',serif] text-[15px] text-[#333] leading-[1.8]">{it}</span>
                  </div>
                ))}
              </div>
              <div className="mt-9">
                <a href="/contact" className={`${BTN08_ON_LIGHT} w-full sm:w-auto`}>Wine Steward Butler資格に申し込む</a>
              </div>
            </div>
            <ul className="mt-6 space-y-2">
              {['本資格は全日本執事協会が認定する民間資格です', 'ソムリエ資格や日本ソムリエ協会の資格とは異なります', '受験資格は20歳以上です（飲酒に関する知識を扱うため）', '資格取得により就職・転職・収入を保証するものではありません', '知識習得と自己研鑽を目的としています'].map((n) => (
                <li key={n} className="flex items-start gap-2.5 font-['Noto_Serif_JP',serif] text-sm text-[#6b6353] leading-[1.85]">
                  <i className="ri-information-line text-[#a38e65] text-base mt-0.5 shrink-0"></i>
                  <span>{n}</span>
                </li>
              ))}
            </ul>
          </section>

          {/* ===== VII. よくある質問（アコーディオン） ===== */}
          <section>
            <span className="-mx-5 sm:-mx-8 lg:-mx-10 mb-6 bg-[#1a2942] border-y border-[#a38e65]/25 px-5 sm:px-8 lg:px-10 py-[18px] block font-['Playfair_Display',serif] uppercase tracking-[0.28em] text-xs text-[#d7b779]">VII. FAQ</span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
              よくある質問
            </h2>
            <div className="border-t border-[#e2ddd2]">
              {faqs.map((f) => (
                <details key={f.q} className="group border-b border-[#e2ddd2]">
                  <summary className="flex items-center justify-between gap-4 cursor-pointer py-5 list-none">
                    <span className="jba-display font-medium text-[#1a2942] text-base sm:text-[17px] leading-[1.6]">{f.q}</span>
                    <i className="ri-add-line text-[#a38e65] text-xl shrink-0 transition-transform group-open:rotate-45"></i>
                  </summary>
                  <p className="font-['Noto_Serif_JP',serif] text-[15px] text-[#4a4638] leading-[1.95] pb-5 pr-8">{f.a}</p>
                </details>
              ))}
            </div>
          </section>

          {/* ===== VIII. 関連資格 + 締めCTA（紺パネル）。由来: about/overview S6 ===== */}
          <section>
            <span className="-mx-5 sm:-mx-8 lg:-mx-10 mb-6 bg-[#1a2942] border-y border-[#a38e65]/25 px-5 sm:px-8 lg:px-10 py-[18px] block font-['Playfair_Display',serif] uppercase tracking-[0.28em] text-xs text-[#d7b779]">VIII. Related Certifications</span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-3">
              あわせて学びたい専門資格
            </h2>
            <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] max-w-3xl mb-8">
              他の専門資格も組み合わせて取得することで、より幅広い専門性を証明できます。
            </p>
            <div className="grid sm:grid-cols-3 gap-5 sm:gap-6 mb-12">
              {related.map((r) => (
                <a key={r.href} href={r.href} className="group block border border-[#e2ddd2] hover:border-[#a38e65] bg-white px-6 py-7 transition-colors">
                  <i className={`${r.icon} text-2xl text-[#a38e65]`}></i>
                  <h3 className="jba-display font-medium text-[#1a2942] text-lg mt-4 mb-1.5 leading-[1.5]">{r.name}</h3>
                  <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.85] mb-4">{r.ja}</p>
                  <span className="inline-flex items-center gap-2 font-['Noto_Serif_JP',serif] text-sm text-[#8f7a56] group-hover:text-[#a38e65] transition-colors">
                    詳しく見る<i className="ri-arrow-right-line transition-transform group-hover:translate-x-1"></i>
                  </span>
                </a>
              ))}
            </div>
            <div className="bg-[#1a2942] border border-[#a38e65]/45 p-8 sm:p-10 lg:p-12">
              <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-[#e7e2d8] leading-[1.95] max-w-3xl mb-8">
                Wine Steward Butler資格について、ご不明な点やお申込みのご相談はお気軽にお問い合わせください。専門資格の全体像は資格制度の一覧からもご覧いただけます。
              </p>
              <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
                <a href="/contact" className={BTN08_ON_NAVY}>お問い合わせ・申込</a>
                <a href="/qualifications" className={BTN08_ON_NAVY}>資格制度の一覧を見る</a>
              </div>
            </div>
          </section>

        </div>
      </div>
    </Layout>
  );
}
